/**
 * Developer docs: /docs (index) + /docs/:slug (page). Public + crawlable.
 * Content lives in src/data/docsPages.ts and is prerendered to static HTML by
 * scripts/dev/gen-content-pages.mjs.
 */
import { BrandMark } from '../components/BrandMark';
import { Link, useParams, Navigate } from 'react-router-dom';
import { docsPages, getDocPage, DocBlock, API_BASE } from '../data/docsPages';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://agent-mesh.org';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/docs" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-lg font-bold">
              Agent<span className="text-brand">Mesh</span> Docs
            </span>
          </Link>
          <Link to="/help" className="text-sm font-medium text-slate-600 hover:text-slate-900">
            Help center
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12">{children}</main>
      <footer className="border-t">
        <div className="mx-auto max-w-3xl px-5 py-8 text-sm text-slate-500">
          <Link to="/docs" className="hover:underline">All docs</Link> ·{' '}
          <Link to="/help" className="hover:underline">Help center</Link> ·{' '}
          <Link to="/" className="hover:underline">Home</Link>
        </div>
      </footer>
    </div>
  );
}

function Block({ block }: { block: DocBlock }) {
  if ('h' in block) return <h2 className="mt-8 mb-2 text-lg font-semibold">{block.h}</h2>;
  if ('p' in block) return <p className="mb-3 text-slate-700">{block.p}</p>;
  if ('code' in block) {
    return (
      <pre className="mb-4 overflow-x-auto rounded-lg bg-slate-900 p-4 text-[13px] leading-relaxed text-slate-100">
        <code>{block.code}</code>
      </pre>
    );
  }
  if ('note' in block) {
    return (
      <p className="mb-4 rounded-lg border-l-4 border-brand bg-slate-50 p-3 text-sm text-slate-700">
        {block.note}
      </p>
    );
  }
  return (
    <ol className="mb-4 list-decimal space-y-1 pl-6 text-slate-700">
      {block.steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  );
}

export function DocsIndex() {
  useSeo({
    title: 'Developer Docs',
    description:
      'AgentMesh developer documentation: REST API for agents and workflows, the six execution modes, agent tools, schedules and webhook triggers, errors and rate limits.',
    canonical: `${SITE}/docs`,
  });

  const categories = [...new Set(docsPages.map((p) => p.category))];

  return (
    <Shell>
      <h1 className="mb-2 text-3xl font-bold">Developer docs</h1>
      <p className="mb-2 text-slate-600">
        Everything in AgentMesh is available over a REST API. Base URL:{' '}
        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm">{API_BASE}</code>
      </p>
      <p className="mb-8 text-sm text-slate-500">
        Prefer click-by-click guides?{' '}
        <Link to="/help" className="text-brand hover:underline">
          Help center
        </Link>
      </p>

      {categories.map((cat) => (
        <div key={cat} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {cat}
          </h2>
          <ul className="space-y-2">
            {docsPages
              .filter((p) => p.category === cat)
              .map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/docs/${p.slug}`}
                    className="block rounded-lg border p-4 hover:border-slate-400"
                  >
                    <span className="font-semibold">{p.title}</span>
                    <p className="mt-1 text-sm text-slate-600">{p.summary}</p>
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </Shell>
  );
}

export function DocsPage() {
  const { slug = '' } = useParams();
  const page = getDocPage(slug);

  useSeo({
    title: page ? `${page.title} · Docs` : 'Developer Docs',
    description: page?.metaDescription || page?.summary,
    canonical: page ? `${SITE}/docs/${page.slug}` : `${SITE}/docs`,
    jsonLd: page
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            headline: page.title,
            description: page.metaDescription || page.summary,
            url: `${SITE}/docs/${page.slug}`,
            publisher: { '@type': 'Organization', name: 'AgentMesh', url: SITE },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Docs', item: `${SITE}/docs` },
              {
                '@type': 'ListItem',
                position: 2,
                name: page.title,
                item: `${SITE}/docs/${page.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!page) return <Navigate to="/docs" replace />;

  return (
    <Shell>
      <p className="mb-2 text-sm font-medium text-slate-500">{page.category}</p>
      <h1 className="mb-6 text-3xl font-bold">{page.title}</h1>
      {page.body.map((block, i) => (
        <Block key={i} block={block} />
      ))}

      <div className="mt-10 border-t pt-6">
        <h2 className="mb-3 text-lg font-semibold">More docs</h2>
        <ul className="space-y-2 text-sm">
          {docsPages
            .filter((p) => p.slug !== page.slug)
            .slice(0, 5)
            .map((p) => (
              <li key={p.slug}>
                <Link to={`/docs/${p.slug}`} className="text-brand hover:underline">
                  {p.title}
                </Link>
              </li>
            ))}
          <li>
            <Link to="/docs" className="text-brand hover:underline">
              All docs →
            </Link>
          </li>
        </ul>
      </div>
    </Shell>
  );
}
